import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SecurityContext,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  GetTransactionCheckImagesRequestParams,
  TransactionClientHttpService,
  TransactionItem,
} from '@backbase/transactions-http-ang';
import { CategoryName, CreditDebitIndicator } from '../enums';
import { NgbCarousel, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { catchError, map, tap } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import { UtilsService } from 'account-selector-widget-extended';

type BillingStatus = 'APROBADA' | 'PENDIENT' | 'INACTIVE' | 'REJECTED' | 'POSTED' | 'PENDING' | 'UNBILLED' | 'BILLED' | 'TEMP' |
  'CLOSED' | 'DECLINE' | 'DECLINES' | 'UNDERWF' | 'FROMS-IN' | 'POSTPONE' | 'SUSPEND' | 'PROCESS' | 'SYSTEM' | 'WAITING';

@Component({
  selector: 'bn-transaction-item-details',
  templateUrl: 'bn-transaction-item-details.component.html',
  styleUrls: ['./bn-transaction-item-details.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BnTransactionItemDetailsComponent implements OnInit, OnChanges, AfterViewInit {
  checkImages$?: Observable<string[]>;
  carouselIsOpened = false;
  _isOpen = false;
  @Input() hostRef: any;
  @Input() set isOpen(status: any) {
    this._isOpen = status;
  }
  @Input() set transaction(val: TransactionItem) {
    this.updateTransaction(val);
  }
  @Output() close = new EventEmitter<boolean>();
  modalOptions: NgbModalOptions = {
    keyboard: false,
    backdrop: 'static',
    centered: true,
    windowClass: 'bn-transaction-item-details__modal',
  };

  isEditingNotes = false;
  isSavingNotes = true;

  _transaction?: TransactionItem;
  bookingDate?: string;
  asettlementDate?: string;
  description?: string;
  checkNumber?: number;
  debitAmount?: string;
  creditAmount?: string;
  balanceAfterTransaction?: string;
  reference?: string;
  typeGroup?: string;
  approvalCode?: string;
  billingStatus?: string;
  ammount?: string;

  showBalance = true;
  showBillingStatus = true;
  showCheckNumber = true;
  showApprovalCode = true;

  saving = false;

  notesForm = new FormGroup({
    notes: new FormControl(''),
  });

  currentNote?: string;

  @ViewChild(NgbCarousel) ngbCarousel?: NgbCarousel;
  loadingCheckImages = false;

  constructor(
    private utils: UtilsService,
    private transactionService: TransactionClientHttpService,
    private changeDetector: ChangeDetectorRef,
    private domSanitizer: DomSanitizer,
  ) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.transaction) {
      this.getCheckImages(this._transaction?.id);
    }
  }

  ngAfterViewInit(): void {
    this.ngbCarousel?.pause();
  }

  getCheckImages(transactionId?: string) {
    if (transactionId) {
      this.loadingCheckImages = true;
      this.checkImages$ = this.transactionService.getTransactionCheckImages({ transactionId }).pipe(
        map((data) => data.images.map((image) => `data:${image.mimeType};base64,${image.content}`)),
        catchError((err) => {
          return of([]);
        }),
        tap(() => {
          this.loadingCheckImages = false;
        }),
      );
    }
  }

  updateTransaction(transaction: TransactionItem) {
    if (!transaction) {
      return false;
    }

    this._transaction = transaction;

    const additions = transaction?.additions;
    this.typeGroup = transaction?.typeGroup;
    this.bookingDate = this.formatDate(transaction?.bookingDate || '');
    this.asettlementDate = this.formatDate(additions?.settlementDate || '');
    this.description = transaction?.description;
    this.checkNumber = transaction?.checkSerialNumber;
    this.approvalCode = additions?.approvalCode;
    this.billingStatus = transaction?.billingStatus;
    this.currentNote = transaction?.notes;
    this.notesForm.get('notes')?.setValue(transaction?.notes);

    if (!!this.creditAmount) {
      if (!isNaN(+this.creditAmount) && +this.creditAmount > 0) {
        this.creditAmount = this.currencyProcesor(
          transaction?.transactionAmountCurrency.currencyCode,
          this.creditAmount,
          transaction?.creditDebitIndicator === CreditDebitIndicator.CRDT ||
            (transaction?.creditDebitIndicator === CreditDebitIndicator.DBIT && false),
        );
      } else {
        this.creditAmount = undefined;
      }
    }

    if (!!this.debitAmount) {
      if (!isNaN(+this.debitAmount) && +this.debitAmount > 0) {
        this.debitAmount = this.currencyProcesor(
          transaction?.transactionAmountCurrency.currencyCode,
          this.debitAmount,
          transaction?.creditDebitIndicator === CreditDebitIndicator.CRDT ||
            (transaction?.creditDebitIndicator === CreditDebitIndicator.DBIT && false),
        );
      } else {
        this.debitAmount = undefined;
      }
    }

    this.ammount = this.currencyProcesor(
      transaction?.transactionAmountCurrency?.currencyCode,
      transaction?.transactionAmountCurrency?.amount,
    );

    this.debitAmount = transaction?.creditDebitIndicator === CreditDebitIndicator.DBIT ? this.ammount : '';
    this.creditAmount = transaction?.creditDebitIndicator === CreditDebitIndicator.CRDT ? this.ammount : '';

    this.balanceAfterTransaction = this.currencyProcesor(
      transaction?.transactionAmountCurrency?.currencyCode,
      additions?.balanceAfterTransaction,
    );

    this.reference = transaction?.reference;

    switch (transaction?.additions?.accountType) {
      case 'kind1': //current
        this.showBillingStatus = false;
        this.showApprovalCode = false;
        break;
      case 'kind2': //savings
        this.showCheckNumber = false;
        this.showBillingStatus = false;
        this.showApprovalCode = false;
        break;
      case 'kind4': //credit card
        this.showCheckNumber = false;
        this.showBalance = false;
        break;
      case 'kind5': //loan
      case 'kind6': //term deposit
        this.showBalance = false;
        this.showCheckNumber = false;
        this.showApprovalCode = false;
        break;
    }

    if (transaction?.billingStatus === 'TEMP') {
      this.showBalance = false;
    }

    this.changeDetector.detectChanges();
  }

  currencyProcesor(currency: string | undefined, amount: number | string | undefined, symbol?: boolean): string {
    const amountFormated = amount ? this.utils.formatNumberCurrency(amount) : undefined;
    const preSymbol = symbol === true ? '+' : symbol === false ? '-' : '';
    return currency && amount ? `${preSymbol} ${currency} ${amountFormated}` : '__ __';
  }

  openDialog(): void {
    this._isOpen = true;
  }

  closeDialog(): void {
    this._isOpen = false;
    this.close.emit(false);
  }

  isEmpty(value: string): boolean {
    return value === null || value === undefined || value === '' || value === '-1';
  }

  updateNote(transaction: TransactionItem, notes: string) {
    this.saving = true;
    this.transactionService
      .patchTransactions({
        transactionsClientPatchRequestBody: [
          {
            id: transaction.id,
            arrangementId: transaction.arrangementId,
            notes,
          },
        ],
      })
      .subscribe(
        () => {
          this.notesForm.get('notes')?.setValue(notes);
          this.currentNote = notes;
          this.isEditingNotes = false;

          if (this._transaction) {
            this._transaction.notes = notes;
          }

          this.saving = false;

          this.changeDetector.detectChanges();
        },
        (error) => {
          this.saving = false;
        },
      );
  }

  deleteNote(transaction: TransactionItem) {
    this.updateNote(transaction, '');
  }

  getCategoryName(value: any) {
    const index = Object.keys(CategoryName).indexOf(value);
    const values = Object.values(CategoryName);
    return values[index];
  }

  cancelUpdateNote() {
    this.isEditingNotes = false;
  }

  openCarousel(side: string) {
    this.ngbCarousel?.select(side);
    this.ngbCarousel?.pause();
    this.carouselIsOpened = true;
  }

  closeCarousel() {
    this.carouselIsOpened = false;
  }

  formatDate(date: string) {
    const strippedDate = date.split('-');

    if (strippedDate[0].length === 4) {
      return `${strippedDate[2]}-${strippedDate[1]}-${strippedDate[0]}`;
    }

    return date;
  }

  mapBillingStatus(billingStatus: BillingStatus): BillingStatus | string {
    if (!(billingStatus === 'PENDIENT' ||
      billingStatus === 'DECLINES' ||
      billingStatus === 'UNDERWF' ||
      billingStatus === 'FROMS-IN')) {
      return billingStatus;
    }
    let mapped: string = '';
    switch (billingStatus) {
      case 'PENDIENT':
        mapped = 'PENDIENTE';
        break;
      case 'DECLINES':
        mapped = 'DECLINE SERVICE';
        break;
      case 'UNDERWF':
        mapped = 'UNDER WORKFLOW';
        break;
      case 'FROMS-IN':
        mapped = 'FROM STAND-IN';
        break;

      default:
        mapped = 'OTHER';
        break;
    }

    return mapped;
  }
}
